import { useState, useRef, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { api } from '@/lib/api';
import type { ApiResponse } from '@/types';
import { MarkdownText } from '@/components/common/MarkdownText';
import { Textarea } from '@/components/ui/textarea';
import { formatAssistantDisplayMarkdown } from '@/lib/format-research-summary';
import {
  Bot, Send, X, Sparkles, Minimize2, Maximize2,
  Copy, Check, Loader2, Play,
} from 'lucide-react';

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  actions?: Array<{ tool: string; dryRun: boolean; input?: Record<string, unknown>; result?: Record<string, unknown> }>;
}

interface AIChatSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  context?: {
    type: string;
    title: string;
    data?: Record<string, unknown>;
  };
}

export function AIChatSidebar({ isOpen, onClose, context }: AIChatSidebarProps) {
  const [threadId, setThreadId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [executingActionKey, setExecutingActionKey] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const sendMessage = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage: ChatMessage = {
      id: crypto.randomUUID(),
      role: 'user',
      content: input.trim(),
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      const res = await api.post<ApiResponse<{ response: string; actions?: Array<{ tool: string; dryRun: boolean; input?: Record<string, unknown>; result?: Record<string, unknown> }> }>>('/ai/chat', {
        message: userMessage.content,
        thread_id: threadId || undefined,
        context: context ? {
          type: context.type,
          title: context.title,
          ...context.data,
        } : undefined,
        history: messages.slice(-10).map(m => ({
          role: m.role,
          content: m.content,
        })),
      });
      if (!res.success) {
        setMessages(prev => [...prev, {
          id: crypto.randomUUID(),
          role: 'assistant',
          content: `Fehler: ${res.error || 'Unbekannter Fehler vom Server.'}`,
          timestamp: new Date(),
        }]);
        return;
      }

      const assistantMessage: ChatMessage = {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: res.data?.response || (res.data as any)?.reply || 'Entschuldigung, ich konnte keine Antwort generieren.',
        timestamp: new Date(),
        actions: (res.data as any)?.actions || [],
      };

      const maybeThread = (res.data as any)?.thread_id as string | undefined;
      if (maybeThread && !threadId) setThreadId(maybeThread);

      setMessages(prev => [...prev, assistantMessage]);
    } catch {
      setMessages(prev => [...prev, {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: 'Fehler bei der Verarbeitung. Bitte versuchen Sie es erneut.',
        timestamp: new Date(),
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = async (text: string, id: string) => {
    await navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const quickActions = [
    { label: 'Neue Kampagne', prompt: 'Erstelle eine neue Kampagne für den aktiven Marketing-Plan mit Zielgruppe und Budget.' },
    { label: 'Kalender-Eintrag', prompt: 'Lege einen Kalender-Reminder für ein Follow-up in 7 Tagen an.' },
    { label: 'Team erweitern', prompt: 'Füge ein neues Teammitglied zum Team Digital Marketing hinzu.' },
    { label: 'Zulieferer anlegen', prompt: 'Erstelle einen neuen Zulieferer mit Kontaktinformationen.' },
  ];

  const executeSuggestedAction = async (messageId: string, actionIndex: number) => {
    const selectedMessage = messages.find((msg) => msg.id === messageId);
    const selectedAction = selectedMessage?.actions?.[actionIndex];
    if (!selectedAction || !threadId || selectedAction.dryRun === false) return;

    const actionKey = `${messageId}:${actionIndex}`;
    setExecutingActionKey(actionKey);
    try {
      const res = await api.post<ApiResponse<Record<string, unknown>>>('/ai/actions/execute', {
        thread_id: threadId,
        tool: selectedAction.tool,
        input: selectedAction.input || {},
        dry_run: false,
      });
      setMessages((prev) =>
        prev.map((msg) => {
          if (msg.id !== messageId || !msg.actions) return msg;
          return {
            ...msg,
            actions: msg.actions.map((action, idx) =>
              idx === actionIndex
                ? {
                    ...action,
                    dryRun: false,
                    result: {
                      ...(action.result || {}),
                      execution: res.success ? (res.data || { status: 'queued' }) : { error: res.error || 'Ausfuehrung fehlgeschlagen' },
                    },
                  }
                : action
            ),
          };
        })
      );
    } finally {
      setExecutingActionKey(null);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className={cn(
        'fixed right-0 top-0 z-50 flex h-full flex-col border-l border-border bg-card shadow-xl transition-all duration-300',
        isExpanded ? 'w-[600px]' : 'w-[400px]'
      )}
    >
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-card-hover dark:bg-primary-hover">
            <Sparkles size={16} className="text-foreground dark:text-muted" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-foreground">KI-Assistent</h3>
            {context && (
              <p className="text-xs text-muted">
                Kontext: {context.title}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="rounded-md p-1.5 text-muted hover:bg-card-hover hover:text-foreground"
          >
            {isExpanded ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
          </button>
          <button
            onClick={onClose}
            className="rounded-md p-1.5 text-muted hover:bg-card-hover hover:text-foreground"
          >
            <X size={14} />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.length === 0 && (
          <div className="flex flex-col items-center py-8 text-center">
            <Bot size={40} className="mb-3 text-muted" />
            <h4 className="text-sm font-medium text-muted">
              Wie kann ich Ihnen helfen?
            </h4>
            <p className="mt-1 text-xs text-muted">
              Ich kann Marketingtexte generieren, Kampagnen planen und bei der Analyse helfen.
            </p>
            <div className="mt-4 flex flex-wrap justify-center gap-2">
              {quickActions.map((action) => (
                <button
                  key={action.label}
                  onClick={() => setInput(action.prompt)}
                  className="rounded-full border border-border px-3 py-1.5 text-xs text-muted hover:border-border hover:bg-card-hover hover:text-foreground dark:hover:bg-primary-hover/20 dark:hover:text-muted"
                >
                  {action.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((message) => (
          <div
            key={message.id}
            className={cn(
              'flex',
              message.role === 'user' ? 'justify-end' : 'justify-start'
            )}
          >
            <div
              className={cn(
                'relative max-w-[85%] rounded-xl px-4 py-2.5 text-sm',
                message.role === 'user'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-card-hover text-foreground'
              )}
            >
              {message.role === 'assistant' ? (
                <MarkdownText
                  content={formatAssistantDisplayMarkdown(message.content)}
                  className="space-y-3 leading-relaxed"
                />
              ) : (
                <div className="whitespace-pre-wrap">{message.content}</div>
              )}
              {message.role === 'assistant' && (message.actions?.length || 0) > 0 && (
                <div className="mt-3 space-y-2 border-t border-border/70 pt-3">
                  {message.actions?.map((action, index) => {
                    const actionKey = `${message.id}:${index}`;
                    const running = actionKey === executingActionKey;
                    return (
                      <div key={actionKey} className="rounded-lg border border-border bg-card/80 p-2 text-xs text-muted dark:bg-card/60 dark:text-foreground">
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-medium">{action.tool}</span>
                          <span className={cn('rounded px-1.5 py-0.5 text-[10px] font-semibold', action.dryRun ? 'bg-warning-soft text-warning' : 'bg-success-soft text-success')}>
                            {action.dryRun ? 'dry-run' : 'ausgefuehrt'}
                          </span>
                        </div>
                        {action.input && (
                          <pre className="mt-2 max-h-24 overflow-auto rounded bg-card-hover p-2 text-[10px] text-muted dark:bg-background">{JSON.stringify(action.input, null, 2)}</pre>
                        )}
                        {action.dryRun && (
                          <button
                            onClick={() => executeSuggestedAction(message.id, index)}
                            disabled={running || !threadId}
                            className="mt-2 inline-flex items-center gap-1 rounded border border-border px-2 py-1 text-[11px] font-medium text-foreground hover:bg-card-hover disabled:opacity-50 dark:text-muted dark:hover:bg-primary-hover/30"
                          >
                            {running ? <Loader2 size={11} className="animate-spin" /> : <Play size={11} />}
                            Ausfuehren
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              {message.role === 'assistant' && (
                <button
                  onClick={() => copyToClipboard(message.content, message.id)}
                  className="absolute -bottom-1 -right-1 rounded-md bg-card p-1 shadow-sm hover:bg-background dark:bg-card-hover dark:hover:bg-muted"
                >
                  {copiedId === message.id ? (
                    <Check size={12} className="text-success" />
                  ) : (
                    <Copy size={12} className="text-muted" />
                  )}
                </button>
              )}
            </div>
          </div>
        ))}

        {isLoading && (
          <div className="flex justify-start">
            <div className="flex items-center gap-2 rounded-xl bg-card-hover px-4 py-3">
              <Loader2 size={14} className="animate-spin text-foreground" />
              <span className="text-xs text-muted">Generiere Antwort...</span>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      <div className="border-t border-border p-4">
        <div className="flex items-end gap-2">
          <div className="min-w-0 flex-1">
            <Textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  sendMessage();
                }
              }}
              placeholder="Nachricht eingeben..."
              rows={2}
              className="min-h-[52px] w-full resize-none rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted focus:border-border focus:outline-none focus:ring-2 focus:ring-border/20 dark:bg-card-hover"
            />
          </div>
          <button
            onClick={sendMessage}
            disabled={!input.trim() || isLoading}
            title="Nachricht senden"
            aria-label="Nachricht senden"
            className={cn(
              'inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-border/40 bg-primary text-primary-foreground shadow-sm transition-all',
              'hover:bg-primary-hover hover:shadow-md active:scale-95',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border/40 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-background',
              'disabled:cursor-not-allowed disabled:border-border disabled:bg-border disabled:text-muted disabled:shadow-none dark:disabled:bg-card-hover'
            )}
          >
            {isLoading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
          </button>
        </div>
      </div>
    </div>
  );
}
